import { Fragment, forwardRef, useEffect, useRef, useState } from 'react';
import { Theme } from '../../types/enums';
import Tab from './Tab/Tab';

import { SelectorType, TabDesign, TabLayout } from './types/enums';
import { SortAndFilterPropType, TabData, TabsProps, TabsTheme } from './types/propType';
import styles from './Tabs.module.scss';

const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      selectorType = SelectorType.UNDERLINE,
      tabList,
      theme = Theme.BLACK,
      selectedTab = 0,
      layout = TabLayout.HORIZONTAL,
      design = TabDesign.THEMED,
      sortAndFilter = false,
      onTabClick,
      sortAndFilterCount = '',
      className,
      autoFocus = false,
    }: TabsProps,
    ref,
  ) => {
    const totalTabs = sortAndFilter ? tabList.length + 1 : tabList.length;
    const [focusedIndex, setFocusedIndex] = useState<number>(selectedTab);
    const isDirtyRef = useRef<boolean>(false);
    const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
      if (autoFocus || isDirtyRef.current) {
        tabRefs.current[focusedIndex]?.focus();
      }
    }, [focusedIndex, autoFocus]);

    const getNextFocusableIndex = (current: number, direction: 1 | -1): number => {
      isDirtyRef.current = true;
      for (let i = 1; i < totalTabs; i++) {
        const next = (current + direction * i + totalTabs) % totalTabs;
        if (next === 0 && sortAndFilter) {
          return next;
        } // Sort & Filter is always enabled
        if (!tabList[next - (sortAndFilter ? 1 : 0)]?.disabled) {
          return next;
        }
      }
      return current; // fallback if all are disabled
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((prev) => getNextFocusableIndex(prev, 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prev) => getNextFocusableIndex(prev, -1));
      } else if (e.key === 'Home') {
        e.preventDefault();
        setFocusedIndex(getNextFocusableIndex(-1, 1));
      } else if (e.key === 'End') {
        e.preventDefault();
        setFocusedIndex(getNextFocusableIndex(totalTabs, -1));
      }
    };

    function handleClick(index: number, id?: string) {
      id ? onTabClick?.(index, id) : onTabClick?.(index);
    }
    const {
      title = 'Sort & Filter',
      count = sortAndFilterCount,
      selected: sortAndFilterSelected,
    } = sortAndFilter as SortAndFilterPropType;
    return (
      <div
        className={`
        ${styles['tabs-container']} 
        ${styles[design === TabDesign.DEFAULT ? 'white' : design]} 
        ${styles[theme]} 
        ${styles[`selector-${selectorType}`]} 
        ${styles[`layout-${layout}`]} 
        ${className}
      `}
        ref={ref}
        role="tablist"
      >
        {sortAndFilter && (
          <span className={styles['tab-sort-filter']}>
            <Tab
              theme={theme}
              selectorType={SelectorType.CIRCLE}
              title={title}
              layout={TabLayout.HORIZONTAL}
              design={TabDesign.PILL}
              icon={'sliders'}
              selected={sortAndFilterSelected ?? selectedTab === 0}
              onTabClick={() => onTabClick?.(0, 'sortFilter')}
              className={styles['tab-sort-filter-content']}
              data-testid="tab-sortAndFilter-testid"
              tabIndex={focusedIndex === 0 ? 0 : -1}
              ref={(el) => (tabRefs.current[0] = el)}
              onKeyDown={handleKeyDown}
            />
            {count && <div className={styles['tab-sort-filter-badge']}>{count}</div>}
          </span>
        )}
        {sortAndFilter && <div className={styles['tab-divider']} data-testid={'tab-divider-testid'}></div>}
        {tabList.map(({ title, icon, iconStyle, id, divider, disabled, dataTabScrollId, tabTheme = theme }, index) => {
          const ind = sortAndFilter ? index + 1 : index;
          return (
            <Fragment key={`${id || ''}-${index + 1}`}>
              <Tab
                theme={tabTheme}
                selectorType={selectorType}
                title={title}
                layout={layout}
                design={design}
                icon={icon}
                iconStyle={iconStyle}
                selected={selectedTab === ind}
                onTabClick={() => {
                  handleClick(ind, id);
                }}
                disabled={disabled}
                dataTabScrollId={dataTabScrollId}
                tabIndex={focusedIndex === ind ? 0 : -1}
                ref={(el) => (tabRefs.current[ind] = el)}
                onKeyDown={handleKeyDown}
              />
              {divider && <div className={styles['tab-divider']}></div>}
            </Fragment>
          );
        })}
      </div>
    );
  },
);
Tabs.displayName = 'Tabs';

export { SelectorType, TabLayout, TabDesign, Tab };
export type { TabData, TabsProps, TabsTheme, SortAndFilterPropType };
export default Tabs;
